import type {
  AIRecipeDetailResult,
  SimplifiedRecipeSuggestion,
} from "../types/ai.ts";

// Basic guards
export const isString = (v: unknown): v is string => typeof v === "string";
export const isNumber = (v: unknown): v is number =>
  typeof v === "number" && !Number.isNaN(v);
export const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

// Generic normalizer primitives
export type Normalizer<T> = (value: unknown, index?: number) => T;

export const asString = (fallback = ""): Normalizer<string> => (value) => (
  isString(value) ? value : fallback
);

export const asOptionalString =
  (): Normalizer<string | undefined> => (value) => (
    isString(value) ? value : undefined
  );

export const asNumber = (fallback = 0): Normalizer<number> => (value) => (
  isNumber(value) ? value : fallback
);

export function asArray<T>(
  item: Normalizer<T>,
  fallback: T[] = [],
): Normalizer<T[]> {
  return (value) => {
    if (!Array.isArray(value)) return fallback;
    const result: T[] = [];
    for (let i = 0; i < value.length; i += 1) {
      result.push(item(value[i], i));
    }
    return result;
  };
}

export function asObject<T extends object>(
  shape: { [K in keyof T]-?: Normalizer<T[K]> },
  defaults?: Partial<T>,
): Normalizer<T> {
  return (value) => {
    const v: Record<string, unknown> = isObject(value)
      ? (value as Record<string, unknown>)
      : {};
    const out: Partial<Record<keyof T, T[keyof T]>> = {
      ...(defaults as Partial<Record<keyof T, T[keyof T]>> | undefined),
    };
    (Object.keys(shape) as Array<keyof T>).forEach((key) => {
      const norm = shape[key];
      out[key] = norm(v[key as string]) as T[typeof key];
    });
    return out as T;
  };
}

export const normalizeSuggestion = asObject<SimplifiedRecipeSuggestion>({
  title: asString(""),
  description: asString(""),
  estimated_minutes: asNumber(0),
  servings: asNumber(0),
  ingredients_description: asString(""),
});

export const normalizeIngredient = asObject<
  { name: string; amount: number; unit: string; notes?: string }
>(
  {
    name: asString(""),
    amount: asNumber(0),
    unit: asString(""),
    notes: asOptionalString(),
  },
);

export const normalizeStep = asObject<
  { number: number; instruction: string; concurrency_note?: string }
>(
  {
    number: asNumber(0),
    instruction: asString(""),
    concurrency_note: asOptionalString(),
  },
);

export const normalizeRecipeDetail = asObject<AIRecipeDetailResult>(
  {
    title: asString(""),
    servings: asNumber(0),
    total_time_minutes: asNumber(0),
    ingredients: asArray(normalizeIngredient, []),
    steps: asArray(normalizeStep, []),
  },
);

// export function normalizeRecipeDetail(
//   partial: Partial<AIRecipeDetailResult>,
//   fallback: { title: string; servings?: number; estimated_minutes?: number },
// ): AIRecipeDetailResult {
//   const ingredients = asArray(normalizeIngredient, [])(partial.ingredients);
//   const steps = asArray(normalizeStep, [])(partial.steps);

//   return {
//     title: isString(partial.title) ? partial.title : fallback.title,
//     servings: isNumber(partial.servings)
//       ? partial.servings
//       : Number(fallback.servings ?? 0),
//     total_time_minutes: isNumber(partial.total_time_minutes)
//       ? partial.total_time_minutes
//       : Number(fallback.estimated_minutes ?? 0),
//     ingredients,
//     steps,
//   };
// }
